import { useState, useEffect } from 'react';
import { Home, Users, Briefcase, Building, Phone, Menu, X, ChevronRight, Sun, Moon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (localStorage.getItem('theme')) {
      return localStorage.getItem('theme') === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const navItems = [
    { name: 'Home', icon: <Home size={20} /> },
    { name: 'Properties', icon: <Building size={20} /> },
    //{ name: 'Services', icon: <Briefcase size={20} /> },
    { name: 'About', icon: <Users size={20} /> },
    { name: 'Contact', icon: <Phone size={20} /> },
  ];

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="font-sans">
      {/* Main Navbar */}
      <nav className={`fixed w-full z-50 py-6 transition-all duration-300 ${
        scrolled 
          ? 'bg-white dark:bg-gray-900 shadow-lg dark:shadow-gray-800/30 py-4 text-gray-800 dark:text-gray-200' 
          : 'bg-transparent py-4 text-white dark:text-gray-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 transform transition-transform hover:scale-105">
              <Building className={`${scrolled ? 'text-blue-800 dark:text-blue-500' : 'text-blue-500 dark:text-blue-400'}`} size={28} />
              <span className={`text-xl font-bold ${scrolled ? 'text-gray-900 dark:text-gray-100' : 'text-white dark:text-gray-100'}`}>Dev Bhoomi Real Estates
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={`#${item.name.toLowerCase()}`}
                  className={`flex items-center space-x-1 font-medium ${
                    scrolled 
                      ? 'text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-400' 
                      : 'text-white dark:text-gray-200 hover:text-blue-200 dark:hover:text-blue-300'
                  } transition-colors`}
                >
                  {item.icon}
                  <span>{item.name}</span>
                </a>
              ))}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`ml-4 p-2 rounded-full transition-all group ${
                  darkMode 
                    ? 'border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black' 
                    : `border-2 ${scrolled ? 'border-blue-600 text-blue-600' : 'border-blue-400 text-blue-400'} hover:bg-blue-600 hover:text-white`
                }`}
                aria-label="Toggle Dark Mode"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                ) : (
                  <Moon className="w-5 h-5 group-hover:-rotate-12 transition-transform" />
                )}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              {!sidebarOpen && (
                <button onClick={toggleSidebar} className={`focus:outline-none ${
                  scrolled ? 'text-blue-800 dark:text-blue-400' : 'text-white dark:text-gray-100'
                }`}>
                  <Menu size={24} />
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar Background */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-30 dark:bg-black dark:bg-opacity-50 z-40 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onClick={toggleSidebar}
      ></div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 w-full md:w-80 bg-white dark:bg-gray-800 z-50 transform transition-transform duration-500 ease-in-out ${sidebarOpen ? 'translate-y-0' : '-translate-y-full'}`}
      >
        {/* Sidebar Header */}
        <div className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Building size={24} />
            <span className="text-lg font-bold">Dev Bhoomi Real Estates</span>
          </div>
          <button onClick={toggleSidebar} className="text-blue-800 dark:text-blue-300 focus:outline-none hover:text-blue-600 dark:hover:text-blue-200 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Sidebar Links */}
        <div className="py-4">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={`#${item.name.toLowerCase()}`}
              className="flex items-center space-x-4 px-6 py-4 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-200 transition-colors duration-300 border-l-4 border-transparent hover:border-blue-600 dark:hover:border-blue-400"
              onClick={() => setSidebarOpen(false)}
            >
              <div>{item.icon}</div>
              <span className="font-medium">{item.name}</span>
              <ChevronRight size={16} className="ml-auto text-blue-400 dark:text-blue-500" />
            </a>
          ))}
          
          {/* Dark mode toggle in sidebar */}
          <div className="px-6 py-4 mt-4 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`flex items-center space-x-3 w-full py-2 px-3 rounded-lg ${
                darkMode 
                  ? 'bg-gray-700 text-yellow-500 hover:bg-gray-600' 
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
              } transition-colors`}
            >
              {darkMode ? (
                <>
                  <Sun className="w-5 h-5" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;